import _ from 'lodash'
import qs from 'qs'
import axios from 'axios'
import { Message, MessageBox } from 'element-ui'
import { getApiPath } from '@/common/api-proxy.js'
import isMaterialWarehouse, {
  isAssetsFactory,
  isCloudClip,
} from '@/common/is-warehouse.js'
import { localLanguage } from '@/common/storage'
import i18n from '@/lang'
import Url from 'url-parse'
import * as ptAuth from '@pt/utils/auth'
import * as whAuth from '@/utils/auth'
import { LogoutType } from '@pt/enums/logs'

// 创建 axios 实例
const service = axios.create({
  baseURL: process.env.BASE_API, // api 的 base_url
  timeout: 600000, // 请求超时时间 response 会返回 errError: timeout of 1000ms exceeded
  withCredentials: true,
})

function isApplicationJson(url) {
  let modulePrefixList = [
    '/demand/',
    '/api/demand/order/',
    '/gmaker/',
    '/advertiser/',
    '/creative/review/',
    '/finance/',
    '/material/basic/',
    '/monitor/mid-whitelist',
    '/ics/',
    '/ai/',
    '/api/ai/',
    '/grant/',
    '/api/user/',
    '/ps/project/create',
    '/veditor/',
  ]

  // pt与素材仓库共用请求器，针对pt的请求，需要加上其他白名单地址
  if (!isMaterialWarehouse()) {
    // prod,pre 请求添加/api前缀，dev,test不需要
    modulePrefixList = modulePrefixList.concat([
      '/login',
      '/api/login',
      '/ce',
      '/api/ce',
    ])
  }

  return modulePrefixList.some((prefix) => url.indexOf(prefix) === 0)
}

function logout(option) {
  if (isMaterialWarehouse() || isAssetsFactory()) {
    whAuth.logout()
  } else {
    ptAuth.logout(option)
  }
}

function getToken() {
  if (isMaterialWarehouse()) {
    return whAuth.getToken()
  } else if (isCloudClip()) {
    // 云剪辑复用 request.js，但是云剪辑没有登录态，所以这里直接返回空字符串
    return ''
  }
  return ptAuth.getToken()
}

function cookRequestConfig(config) {
  const pathname = new Url(config.url).pathname

  // 动态参数对外平台接口映射
  if (!isMaterialWarehouse() && getApiPath(pathname)) {
    config.url = config.url.replace(pathname, getApiPath(pathname))
  }

  // 如果参数值为空字符串，那么不传该参数
  if (config.params) {
    _.each(config.params, (value, key) => {
      if (value === '') delete config.params[key]
    })
  }

  const lang = localLanguage.get()
  // http header 加语言
  if (lang) {
    config.headers['Accept-Language'] = lang
  }

  const token = getToken()
  // http header 加 token
  if (token && typeof token === 'string') {
    config.headers['Auth-Token'] = token
  }

  if (config.method.toLowerCase() === 'post') {
    // 逐步替换 post 请求 Content-Type（改为 application/json）
    if (isApplicationJson(new Url(config.url).pathname)) {
      config.headers['Content-Type'] = 'application/json'
    }

    // 没有传 Content-Type，设置认为是 application/x-www-form-urlencoded 且将 config.data 转换为合法的格式
    if (!config.headers['Content-Type']) {
      config.data = qs.stringify(config.data)
      config.headers['Content-Type'] = 'application/x-www-form-urlencoded'
    }
  }

  return config
}

function permissionDecorator(wrapper) {
  // eslint-disable-next-line
  return function () {
    // permissionManage.apply(null, arguments)
    return wrapper.apply(null, arguments)
  }
}

// request 拦截器
service.interceptors.request.use(
  permissionDecorator(cookRequestConfig), // 先检查权限，权限不通过直接抛出错误
  (error) => {
    Promise.reject(error)
  },
)

function errorLog(res) {
  console.error(i18n.t('common.request.error'), res) // for debug
}

function promptPermissionError(msg) {
  MessageBox.confirm(msg, i18n.t('common.request.permissionError.title'), {
    confirmButtonText: i18n.t('playTurbo.common.confirmButtonText'),
    cancelButtonText: i18n.t('playTurbo.common.cancelButtonText'),
    type: 'error',
  }).catch()
}

const codeMessage = {
  2: i18n.t('common.request.codeMessage.message1'),
  4: i18n.t('common.request.codeMessage.message2'),
  1: i18n.t('common.request.codeMessage.message3'),
}

// respone 拦截器
service.interceptors.response.use(
  (res) => {
    // 是否走响应拦截器逻辑
    if (res.config.useResponseInterceptors === false) {
      return res
    }

    const data = res.data
    const code = data.code
    const config = res.config

    // 0: 正常，1：其他错误，2：未登录，4：权限错误，3：重定向

    // 未登录或 token 失效去读取接口
    if (code === 2) {
      const errorMessage = data.msg || codeMessage[code]
      logout({
        type: LogoutType.APIError,
        msg: `url: ${config.url}, error: ${errorMessage}`,
      })
      errorLog(res)
      Message({
        message: errorMessage,
        type: 'error',
        duration: 5 * 1000,
        showClose: true,
      })
      return Promise.reject(Object.assign({}, res, { message: errorMessage }))
    }

    // 权限错误
    if (code === 4) {
      const errorMessage = data.msg || codeMessage[code]
      promptPermissionError(errorMessage)
      errorLog(res)
      return Promise.reject(Object.assign({}, res, { message: errorMessage }))
    }

    // 非 0 都是其他错误
    // 特殊类型 600002: 代表资源库的资源已经被删除，需要业务特殊处理，不需要提示错误
    if (code !== 0 && code !== 600002) {
      const errorMessage = data.msg || codeMessage[1]
      // withoutError 1:业务环境不需要公共拦截提示错误，需要针对业务单独处理错误信息
      if (!config.withoutError) {
        Message({
          message: errorMessage,
          type: 'error',
          duration: 5 * 1000,
          showClose: true,
        })
      }
      errorLog(res)
      return Promise.reject(Object.assign({}, res, { message: errorMessage }))
    }

    return res
  },

  (error) => {
    // 由于项目初始化时就统一加了错误弹窗，但是取消请求时不需要弹窗错误提示，所以只能暂时这么处理
    // TODO: 优化请求取消处理方式
    if (error.message === 'cancel request') {
      // do something
      console.log('接口请求被取消')
    } else {
      if (!error.config.withoutError) {
        Message({
          message: error.message || i18n.t('common.request.networkError'),
          type: 'error',
          duration: 5 * 1000,
          showClose: true,
        })
      }
    }

    errorLog(error)
    return Promise.reject(error)
  },
)

export default service
